/**
 * Care Plan Data Model — Enterprise Specification
 *
 * 5 care plan types, 18 sections aligned to Strengthened Quality Standards 1, 3, 5,
 * Aged Care Act 2024, AN-ACC, NQIP.
 *
 * Entities: CarePlan, CarePlanSection, CarePlanNeed, CarePlanGoal, GoalProgress,
 *           CarePlanIntervention, CarePlanEvaluation, InterventionLibraryEntry.
 */

// ── Enums ───────────────────────────────────────────────────────────────

export const CARE_PLAN_STATUS = Object.freeze({
  DRAFT: 'draft',
  ACTIVE: 'active',
  UNDER_REVIEW: 'under_review',
  SUPERSEDED: 'superseded',
  ARCHIVED: 'archived',
});

export const CARE_PLAN_TYPE = Object.freeze({
  COMPREHENSIVE: 'comprehensive',
  SUMMARY: 'summary',
  BEHAVIOUR_SUPPORT: 'behaviour_support',
  PALLIATIVE: 'palliative',
  INTERIM: 'interim',
});

export const SECTION_STATUS = Object.freeze({
  AUTO_GENERATED: 'auto_generated',
  REVIEWED: 'reviewed',
  AMENDED: 'amended',
  NOT_APPLICABLE: 'not_applicable',
});

export const NEED_SOURCE = Object.freeze({
  ASSESSMENT_AUTO: 'assessment_auto',
  CLINICAL_OBSERVATION: 'clinical_observation',
  RESIDENT_REPORTED: 'resident_reported',
  FAMILY_REPORTED: 'family_reported',
});

export const NEED_PRIORITY = Object.freeze({ LOW: 'low', MEDIUM: 'medium', HIGH: 'high', CRITICAL: 'critical' });
export const NEED_STATUS = Object.freeze({ ACTIVE: 'active', RESOLVED: 'resolved', ONGOING: 'ongoing' });

export const GOAL_TYPE = Object.freeze({
  MAINTENANCE: 'maintenance', IMPROVEMENT: 'improvement', PREVENTION: 'prevention',
  COMFORT: 'comfort', REABLEMENT: 'reablement',
});

export const GOAL_DOMAIN = Object.freeze({
  PHYSICAL: 'physical', COGNITIVE: 'cognitive', EMOTIONAL: 'emotional',
  SOCIAL: 'social', SPIRITUAL: 'spiritual', INDEPENDENCE: 'independence',
  SAFETY: 'safety', NUTRITION: 'nutrition', PAIN: 'pain', CONTINENCE: 'continence',
});

export const GOAL_STATUS = Object.freeze({
  NOT_STARTED: 'not_started', IN_PROGRESS: 'in_progress', ACHIEVED: 'achieved',
  PARTIALLY_ACHIEVED: 'partially_achieved', NOT_ACHIEVED: 'not_achieved',
  MODIFIED: 'modified', DISCONTINUED: 'discontinued',
});

export const PROGRESS_RATING = Object.freeze({ IMPROVING: 'improving', STABLE: 'stable', DECLINING: 'declining' });

export const EVIDENCE_TYPE = Object.freeze({
  OBSERVATION: 'observation', MEASUREMENT: 'measurement', ASSESSMENT_SCORE: 'assessment_score',
  INCIDENT_ABSENCE: 'incident_absence', RESIDENT_FEEDBACK: 'resident_feedback',
});

export const INTERVENTION_CATEGORY = Object.freeze({
  NURSING: 'nursing', MEDICAL: 'medical', ALLIED_HEALTH: 'allied_health',
  DIETARY: 'dietary', LIFESTYLE: 'lifestyle', ENVIRONMENTAL: 'environmental',
  MONITORING: 'monitoring', REFERRAL: 'referral', MEDICATION: 'medication', EQUIPMENT: 'equipment',
});

export const INTERVENTION_STATUS = Object.freeze({
  ACTIVE: 'active', COMPLETED: 'completed', DISCONTINUED: 'discontinued', ON_HOLD: 'on_hold',
});

export const INTERVENTION_SOURCE = Object.freeze({
  AUTO_GENERATED: 'auto_generated', MANUAL: 'manual', INTERVENTION_LIBRARY: 'intervention_library',
  ALLIED_HEALTH_RECOMMENDATION: 'allied_health_recommendation', GP_ORDER: 'gp_order',
});

export const RISK_LEVEL = Object.freeze({ LOW: 'low', MEDIUM: 'medium', HIGH: 'high', VERY_HIGH: 'very_high' });

export const REVIEW_OUTCOME = Object.freeze({
  NO_CHANGES: 'no_changes', MINOR_UPDATE: 'minor_update',
  MAJOR_UPDATE: 'major_update', ESCALATED: 'escalated',
});

export const EVALUATION_OUTCOME = Object.freeze({
  SECTION_UNCHANGED: 'section_unchanged', SECTION_UPDATED: 'section_updated', SECTION_ESCALATED: 'section_escalated',
});

export const GENERATED_FROM = Object.freeze({
  AUTO_ASSESSMENT: 'auto_assessment', MANUAL: 'manual', TEMPLATE: 'template',
});

// ── 18 Care Plan Sections (new spec) ────────────────────────────────────

export const CARE_PLAN_SECTIONS = Object.freeze([
  { number: 1,  key: 'person_centred_overview',     title: 'Person-Centred Overview & Goals',      standard: '1.1, 3.1' },
  { number: 2,  key: 'medical_history',              title: 'Medical History & Diagnoses',           standard: '5.4' },
  { number: 3,  key: 'daily_routine',                title: 'Daily Routine & Preferences',           standard: '1.1, 1.3' },
  { number: 4,  key: 'mobility_function',            title: 'Mobility & Function',                   standard: '5.4, 5.5' },
  { number: 5,  key: 'falls_prevention',             title: 'Safety: Falls Prevention',              standard: '5.5' },
  { number: 6,  key: 'skin_integrity',               title: 'Skin Integrity & Wound Management',     standard: '5.5' },
  { number: 7,  key: 'medication_management',        title: 'Medication Management',                 standard: '5.3' },
  { number: 8,  key: 'nutrition_hydration',           title: 'Nutrition & Hydration',                 standard: '5.5, 6.1-6.4' },
  { number: 9,  key: 'cultural_spiritual',           title: 'Cultural & Spiritual Care',             standard: '1.1' },
  { number: 10, key: 'communication',                title: 'Communication',                         standard: '1.1, 3.3' },
  { number: 11, key: 'cognitive_dementia',           title: 'Cognitive Care & Dementia Support',     standard: '5.6' },
  { number: 12, key: 'mental_health',                title: 'Mental Health & Emotional Wellbeing',   standard: '5.5' },
  { number: 13, key: 'pain_management',              title: 'Pain Management',                       standard: '5.5' },
  { number: 14, key: 'continence',                   title: 'Continence Management',                 standard: '5.5' },
  { number: 15, key: 'oral_health',                  title: 'Oral Health',                           standard: '5.5' },
  { number: 16, key: 'sensory_communication_aids',   title: 'Sensory & Communication Aids',          standard: '5.5' },
  { number: 17, key: 'palliative_eol',               title: 'Palliative & End-of-Life Care',         standard: '5.7' },
  { number: 18, key: 'sleep_rest',                   title: 'Sleep & Rest',                          standard: '5.5' },
]);

// ── Assessment → Section mapping (from spec Part 4.2) ──────────────────

export const ASSESSMENT_SECTION_MAP = Object.freeze({
  'my-life-story':              [1, 3, 8, 9, 10],
  'goals-of-care':              [1],
  'comprehensive-initial':      [1, 2],
  'falls-risk':                 [4, 5],
  'falls-risk-assessment':      [4, 5],
  'mobility-functional':        [4, 5],
  'braden_scale':               [6],
  'braden-scale':               [6],
  'pressure-injury-risk':       [6],
  'skin-integrity':             [6],
  'wound-assessment':           [6],
  'iad-assessment':             [6, 14],
  'medication-reconciliation':  [2, 5, 7],
  'medication-review':          [7],
  'psychotropic-review':        [7],
  'antimicrobial-stewardship':  [7],
  'nutritional-screening':      [8],
  'must':                       [8],
  'mna':                        [8],
  'dysphagia':                  [8],
  'iddsi':                      [8],
  'spiritual-cultural':         [9],
  'sensory':                    [5, 10, 16],
  'cognitive':                  [10, 11],
  'mmse':                       [11],
  'rudas':                      [11],
  'delirium-4at':               [11],
  'behaviour':                  [11],
  'restraint':                  [11],
  'depression-screening':       [12],
  'gds':                        [12],
  'cornell-depression':         [12],
  'social-emotional-wellbeing': [12],
  'pain-assessment':            [13],
  'abbey_pain':                 [13],
  'abbey-pain':                 [13],
  'continence-assessment':      [14],
  'oral-health':                [15],
  'sleep-assessment':           [18],
  'palliative-care-needs':      [17],
  'advance-care-planning':      [17],
  'clinical-frailty':           [2],
  'infection-control':          [6],
  'anacc':                      [2, 4, 5, 6, 7, 8, 11, 13, 14, 15],
});

// ── Review constants ────────────────────────────────────────────────────

export const REVIEW_INTERVAL_WEEKS = 8;
export const REVIEW_INTERVAL_MS = REVIEW_INTERVAL_WEEKS * 7 * 24 * 60 * 60 * 1000;
export const REVIEW_LOOKAHEAD_MS = 7 * 24 * 60 * 60 * 1000;
export const INTERIM_PLAN_MAX_HOURS = 48;
export const BSP_REVIEW_INTERVAL_WEEKS = 13; // 3-monthly

// ── ID helpers ──────────────────────────────────────────────────────────

const _uid = (prefix) => `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

// ── Factory: CarePlanNeed ───────────────────────────────────────────────

export const createCarePlanNeed = (data = {}) => ({
  id: data.id || _uid('NEED'),
  sectionId: data.sectionId || null,
  description: data.description || '',
  source: data.source || NEED_SOURCE.ASSESSMENT_AUTO,
  sourceAssessmentId: data.sourceAssessmentId || null,
  identifiedDate: data.identifiedDate || new Date().toISOString().slice(0, 10),
  priority: data.priority || NEED_PRIORITY.MEDIUM,
  status: data.status || NEED_STATUS.ACTIVE,
  resolvedDate: data.resolvedDate || null,
  resolvedBy: data.resolvedBy || null,
});

// ── Factory: GoalProgress ───────────────────────────────────────────────

export const createGoalProgress = (data = {}) => ({
  id: data.id || _uid('GPROG'),
  goalId: data.goalId || null,
  date: data.date || new Date().toISOString().slice(0, 10),
  progressNotes: data.progressNotes || '',
  progressRating: data.progressRating || PROGRESS_RATING.STABLE,
  documentedBy: data.documentedBy || null,
  relatedProgressNoteId: data.relatedProgressNoteId || null,
  evidenceType: data.evidenceType || EVIDENCE_TYPE.OBSERVATION,
  evidenceDetail: data.evidenceDetail || '',
});

// ── Factory: CarePlanGoal ───────────────────────────────────────────────

export const createCarePlanGoal = (data = {}) => ({
  id: data.id || _uid('GOAL'),
  sectionId: data.sectionId || null,
  goalText: data.goalText || '',
  goalType: data.goalType || GOAL_TYPE.MAINTENANCE,
  domain: data.domain || GOAL_DOMAIN.PHYSICAL,
  targetDate: data.targetDate || new Date(Date.now() + REVIEW_INTERVAL_MS).toISOString().slice(0, 10),
  measurable: data.measurable || '',
  residentVoice: data.residentVoice || '',
  status: data.status || GOAL_STATUS.NOT_STARTED,
  statusUpdatedAt: data.statusUpdatedAt || null,
  statusUpdatedBy: data.statusUpdatedBy || null,
  statusNotes: data.statusNotes || '',
  progressEntries: data.progressEntries || [],
  createdAt: data.createdAt || new Date().toISOString(),
});

// ── Factory: CarePlanIntervention ────────────────────────────────────────

export const createCarePlanIntervention = (data = {}) => ({
  id: data.id || _uid('INTV'),
  sectionId: data.sectionId || null,
  interventionText: data.interventionText || '',
  category: data.category || INTERVENTION_CATEGORY.NURSING,
  frequency: data.frequency || 'Every shift',
  responsibleRole: data.responsibleRole || 'RN',
  startDate: data.startDate || new Date().toISOString().slice(0, 10),
  endDate: data.endDate || null,
  status: data.status || INTERVENTION_STATUS.ACTIVE,
  discontinuedReason: data.discontinuedReason || '',
  discontinuedDate: data.discontinuedDate || null,
  discontinuedBy: data.discontinuedBy || null,
  source: data.source || INTERVENTION_SOURCE.AUTO_GENERATED,
  sourceInterventionId: data.sourceInterventionId || null,
  evidenceBasis: data.evidenceBasis || '',
  isHighlighted: data.isHighlighted || false,
  generatesRecurringTask: data.generatesRecurringTask || false,
  taskFrequency: data.taskFrequency || null,
  taskAssigneeRole: data.taskAssigneeRole || null,
});

// ── Factory: CarePlanEvaluation ─────────────────────────────────────────

export const createCarePlanEvaluation = (data = {}) => ({
  id: data.id || _uid('EVAL'),
  sectionId: data.sectionId || null,
  reviewDate: data.reviewDate || new Date().toISOString().slice(0, 10),
  reviewedBy: data.reviewedBy || null,
  evaluationText: data.evaluationText || '',
  goalsAchieved: data.goalsAchieved || 0,
  goalsNotAchieved: data.goalsNotAchieved || 0,
  goalsModified: data.goalsModified || 0,
  interventionsAdded: data.interventionsAdded || 0,
  interventionsDiscontinued: data.interventionsDiscontinued || 0,
  riskLevelChange: data.riskLevelChange || 'unchanged',
  newAssessmentTriggered: data.newAssessmentTriggered || false,
  newAssessmentId: data.newAssessmentId || null,
  outcome: data.outcome || EVALUATION_OUTCOME.SECTION_UNCHANGED,
});

// ── Factory: CarePlanSection ────────────────────────────────────────────

export const createCarePlanSection = (data = {}) => {
  const sectionDef = CARE_PLAN_SECTIONS.find(s => s.number === data.number) || {};
  return {
    id: data.id || _uid('SEC'),
    carePlanId: data.carePlanId || null,
    sectionNumber: data.number || data.sectionNumber || 1,
    sectionKey: data.key || data.sectionKey || sectionDef.key || '',
    title: data.title || sectionDef.title || '',
    status: data.status || SECTION_STATUS.AUTO_GENERATED,
    assessmentSources: data.assessmentSources || [],
    needs: (data.needs || []).map(n => typeof n === 'string' ? createCarePlanNeed({ description: n }) : n),
    goals: (data.goals || []).map(g => typeof g === 'string' ? createCarePlanGoal({ goalText: g }) : g),
    interventions: (data.interventions || []).map(i =>
      typeof i === 'string' ? createCarePlanIntervention({ interventionText: i }) : i
    ),
    evaluations: data.evaluations || [],
    riskLevel: data.riskLevel || RISK_LEVEL.LOW,
    clinicalAlerts: data.clinicalAlerts || [],
    additionalNotes: data.additionalNotes || '',
    residentPreferences: data.residentPreferences || '',
    familyInput: data.familyInput || '',
    lastReviewedAt: data.lastReviewedAt || null,
    lastReviewedBy: data.lastReviewedBy || null,
    reviewNotes: data.reviewNotes || '',
  };
};

// ── Factory: CarePlan ───────────────────────────────────────────────────

export const createCarePlan = (data = {}) => {
  const ts = new Date().toISOString();
  const isInterim = data.type === CARE_PLAN_TYPE.INTERIM;
  const intervalMs = isInterim ? INTERIM_PLAN_MAX_HOURS * 3600 * 1000 : REVIEW_INTERVAL_MS;
  const reviewDueAt = new Date(Date.now() + intervalMs).toISOString();

  return {
    id: data.id || _uid('CP'),
    residentId: data.residentId ? String(data.residentId) : null,
    type: data.type || CARE_PLAN_TYPE.COMPREHENSIVE,
    version: data.version || 1,
    status: data.status || CARE_PLAN_STATUS.DRAFT,
    sections: data.sections || CARE_PLAN_SECTIONS.map(s => createCarePlanSection(s)),

    generatedFrom: data.generatedFrom || GENERATED_FROM.MANUAL,
    assessmentInstanceIds: data.assessmentInstanceIds || [],
    generatedAt: data.generatedAt || ts,

    reviewDueAt: data.reviewDueAt || reviewDueAt,
    lastReviewedAt: data.lastReviewedAt || null,
    lastReviewedBy: data.lastReviewedBy || null,
    reviewOutcome: data.reviewOutcome || null,
    nextReviewDueAt: data.nextReviewDueAt || null,

    signedOffBy: data.signedOffBy || null,
    signedOffAt: data.signedOffAt || null,
    coSignedBy: data.coSignedBy || null,

    previousVersionId: data.previousVersionId || null,
    supersededAt: data.supersededAt || null,
    supersededReason: data.supersededReason || '',

    familyPortalVisible: data.familyPortalVisible != null ? data.familyPortalVisible : true,
    familyPortalSharedAt: data.familyPortalSharedAt || null,
    familyAcknowledgedAt: data.familyAcknowledgedAt || null,

    createdAt: data.createdAt || ts,
    createdBy: data.createdBy || null,
    updatedAt: data.updatedAt || ts,
    updatedBy: data.updatedBy || null,
  };
};

// ── Factory: InterventionLibraryEntry ───────────────────────────────────

export const createInterventionLibraryEntry = (data = {}) => ({
  id: data.id || _uid('ILIB'),
  organisationId: data.organisationId || null,
  sectionKey: data.sectionKey || '',
  triggerCondition: data.triggerCondition || {},
  interventionText: data.interventionText || '',
  category: data.category || INTERVENTION_CATEGORY.NURSING,
  frequency: data.frequency || 'Every shift',
  responsibleRole: data.responsibleRole || 'PCA',
  evidenceBasis: data.evidenceBasis || '',
  isDefault: data.isDefault != null ? data.isDefault : true,
  sortOrder: data.sortOrder || 0,
  status: data.status || 'active',
});

// ── Unscheduled review trigger reasons ──────────────────────────────────

export const UNSCHEDULED_REVIEW_TRIGGERS = Object.freeze([
  { event: 'fall',                  sections: [5],          urgency: 'standard',  label: 'Review falls prevention section' },
  { event: 'fall_with_injury',      sections: [5],          urgency: 'urgent',    label: 'Urgent care plan review — falls with injury' },
  { event: 'hospitalisation_return', sections: null,         urgency: 'urgent',    label: 'Post-hospital care plan review — all sections' },
  { event: 'weight_change',         sections: [8],          urgency: 'standard',  label: 'Review nutrition section' },
  { event: 'new_pressure_injury',   sections: [6],          urgency: 'standard',  label: 'Review skin integrity section' },
  { event: 'new_diagnosis',         sections: [2],          urgency: 'standard',  label: 'Review care plan — new diagnosis impact' },
  { event: 'medication_change',     sections: [7, 5, 13, 14], urgency: 'standard', label: 'Review medication and cross-reference sections' },
  { event: 'behaviour_escalation',  sections: [11],         urgency: 'standard',  label: 'Review cognitive/behaviour section' },
  { event: 'palliative_activation', sections: [17],         urgency: 'urgent',    label: 'Develop palliative care plan' },
  { event: 'cognitive_decline',     sections: [11],         urgency: 'standard',  label: 'Review cognitive section + decision-making capacity' },
  { event: 'family_request',        sections: null,         urgency: 'standard',  label: 'Family-requested care plan review' },
  { event: 'resident_request',      sections: null,         urgency: 'standard',  label: 'Resident-requested care plan review' },
  { event: 'sirs_priority_1',       sections: null,         urgency: 'urgent',    label: 'Urgent care plan review — SIRS incident' },
  { event: 'anacc_reclassification', sections: null,        urgency: 'standard',  label: 'Review care plan — AN-ACC class changed' },
]);

// ── Summary care plan field extraction mapping ──────────────────────────

export const SUMMARY_EXTRACTION_MAP = Object.freeze({
  alerts:         { sections: [2, 5, 6, 7, 11, 17], field: 'clinicalAlerts' },
  diet:           { sections: [8],  fields: ['needs', 'interventions', 'residentPreferences'] },
  mobility:       { sections: [4],  fields: ['needs', 'interventions'] },
  continence:     { sections: [14], fields: ['needs', 'interventions'] },
  communication:  { sections: [10], fields: ['needs', 'interventions'] },
  preferences:    { sections: [1, 3, 18], fields: ['residentPreferences'] },
  keyMeds:        { sections: [7],  fields: ['clinicalAlerts', 'interventions'] },
  goals:          { sections: [1],  fields: ['goals'] },
});

export default {
  createCarePlan, createCarePlanSection, createCarePlanNeed, createCarePlanGoal,
  createGoalProgress, createCarePlanIntervention, createCarePlanEvaluation,
  createInterventionLibraryEntry,
  CARE_PLAN_STATUS, CARE_PLAN_TYPE, CARE_PLAN_SECTIONS, SECTION_STATUS,
  ASSESSMENT_SECTION_MAP, REVIEW_INTERVAL_WEEKS, REVIEW_INTERVAL_MS,
  REVIEW_LOOKAHEAD_MS, RISK_LEVEL, NEED_SOURCE, NEED_PRIORITY, NEED_STATUS,
  GOAL_TYPE, GOAL_DOMAIN, GOAL_STATUS, PROGRESS_RATING, EVIDENCE_TYPE,
  INTERVENTION_CATEGORY, INTERVENTION_STATUS, INTERVENTION_SOURCE,
  REVIEW_OUTCOME, EVALUATION_OUTCOME, GENERATED_FROM,
  UNSCHEDULED_REVIEW_TRIGGERS, SUMMARY_EXTRACTION_MAP,
  INTERIM_PLAN_MAX_HOURS, BSP_REVIEW_INTERVAL_WEEKS,
};
